import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { HoursInput } from "@/components/attendance/HoursInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BookOpen, Play, ClipboardCheck, QrCode, ArrowRight, Clock } from "lucide-react";
import { courseYears, yearsLabel } from "@/lib/school";
import { YearLevelsPicker } from "@/components/attendance/YearLevelsPicker";

export default function DocentePresenze() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [duration, setDuration] = useState(2);
  const [years, setYears] = useState({});
  const yearsFor = (c) => years[c.course_id] ?? courseYears(c);

  const { data: summary = [], isLoading } = useQuery({
    queryKey: ["teacher-portal-attendance"],
    queryFn: async () => (await api.get("/teacher-portal/attendance-summary")).data,
  });

  const createSession = useMutation({
    mutationFn: async ({ courseId, durationHours, yearLevels }) => {
      const today = new Date().toLocaleDateString("en-CA");
      return (await api.post("/attendance/sessions", {
        course_id: courseId,
        date: today,
        duration_hours: durationHours,
        year_levels: yearLevels,
      })).data;
    },
    onSuccess: (s) => {
      toast.success("Sessione aperta");
      qc.invalidateQueries({ queryKey: ["teacher-portal-attendance"] });
      nav(`/docente/presenze/${s.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore apertura sessione"),
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="docente-presenze">
      <PageHeader
        title="Presenze in aula"
        subtitle="Apri una sessione di presenza per ogni tuo corso e segna gli studenti dall'aula."
      />

      <Card className="p-4 border border-stone-200 rounded-xl shadow-none mb-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Durata lezione di oggi (ore)</Label>
          <div className="text-[11px] text-stone-500 mt-0.5">
            Le ore incidono sul calcolo della percentuale di presenza dello studente rispetto al monte ore totale del corso.
          </div>
        </div>
        <HoursInput
          value={duration}
          onChange={setDuration}
          className="w-32"
          testId="lesson-duration"
        />
      </Card>

      {isLoading ? (
        <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>
      ) : summary.length === 0 ? (
        <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">
          Non risultano corsi assegnati al tuo profilo docente.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {summary.map((c) => (
            <Card key={c.course_id} className="border border-stone-200 rounded-xl shadow-none overflow-hidden" data-testid={`docente-presenze-course-${c.course_id}`}>
              <div className="p-4 border-b border-stone-100 bg-[#FDFBF7] flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 grid place-items-center text-[#1C352D] shrink-0">
                  <BookOpen size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1C352D] truncate flex items-center gap-2">
                    {c.course_name}
                    {c.is_seminar && (
                      <Badge className="bg-[#E2725B]/10 text-[#8f3e26] border border-[#E2725B]/30 text-[10px] rounded-full">SEMINARIO</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap gap-2">
                    {c.course_code && <span className="mono">{c.course_code}</span>}
                    {yearsLabel(c) && <span>· {yearsLabel(c)}</span>}
                    {c.cfu != null && <span>· {c.cfu} CFU</span>}
                    {c.course_hours != null && <span>· monte ore <b className="text-[#1C352D]">{c.course_hours}h</b></span>}
                  </div>
                </div>
              </div>
              <div className="p-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Lezioni</div>
                  <div className="text-lg font-semibold mt-0.5">{c.total_sessions}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Ore erogate</div>
                  <div className="text-lg font-semibold mt-0.5">{c.total_recorded_hours || 0}h</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Tasso medio</div>
                  <div className="text-lg font-semibold mt-0.5">{c.attendance_rate}%</div>
                </div>
              </div>
              {c.course_hours != null && c.course_hours > 0 && (
                <div className="px-4 pb-3">
                  <div className="w-full h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#1C352D] transition-all"
                      style={{ width: `${Math.min(100, (c.total_recorded_hours / c.course_hours) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-stone-500 mt-1 text-right">
                    {Math.round((c.total_recorded_hours / c.course_hours) * 100)}% del monte ore erogato
                  </div>
                </div>
              )}
              <div className="p-4 border-t border-stone-100 flex flex-wrap gap-2">
                {c.today_session_id ? (
                  <>
                    <Button
                      onClick={() => nav(`/docente/presenze/${c.today_session_id}`)}
                      className="rounded-full bg-[#1C352D] hover:bg-[#264A3E] text-white transition-colors duration-150"
                      data-testid={`open-today-session-${c.course_id}`}
                    >
                      <ClipboardCheck size={14} className="mr-1.5"/> Sessione di oggi
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => nav(`/docente/presenze/${c.today_session_id}/qr`)}
                      className="rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white transition-colors duration-150"
                    >
                      <QrCode size={14} className="mr-1.5"/> QR
                    </Button>
                  </>
                ) : (
                  <>
                  <div className="w-full">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5">Studenti di quali anni?</div>
                    <YearLevelsPicker
                      value={yearsFor(c)}
                      onChange={(v) => setYears({ ...years, [c.course_id]: v })}
                      testId={`docente-years-${c.course_id}`}
                    />
                  </div>
                  <Button
                    onClick={() => createSession.mutate({ courseId: c.course_id, durationHours: duration, yearLevels: yearsFor(c) })}
                    disabled={createSession.isPending || !(duration > 0 && duration <= 24)}
                    className="rounded-full bg-[#E2725B] hover:bg-[#c85d47] text-white transition-colors duration-150"
                    data-testid={`open-new-session-${c.course_id}`}
                  >
                    <Play size={14} className="mr-1.5"/>
                    {createSession.isPending ? "Apertura…" : "Apri sessione oggi"}
                    <Clock size={12} className="ml-2 opacity-70"/>
                    <span className="text-[11px] opacity-90 ml-1">{String(duration ?? "").replace(".", ",")}h</span>
                  </Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
